import { API_BASE_URL } from "$lib/config/index.js";
import { writable } from "svelte/store";

import { ajax } from "../ajax";

type NavbarStore = {
    todoCount: number;
    verifiedCount: number;
    doneCount: number;
    overdueCount: number;
    totalDictationsCount: number;
};

// Define the expected response type
interface NavbarResponse {
    dictations: {
        unverified: number;
        verified: number;
        done: number;
        overdue: number;
        total: number;
    };
}

const createDebugStore = () => {
    const { subscribe, set, update } = writable<NavbarStore>({
        todoCount: 0,
        verifiedCount: 0,
        doneCount: 0,
        overdueCount: 0,
        totalDictationsCount: 0,
    });

    // Keep track of the last source that updated the counts
    let lastUpdateSource = "initial";
    let lastUpdateTime = Date.now();
    let updateCounter = 0;

    return {
        subscribe,
        set: (value: NavbarStore) => {
            return set(value);
        },
        update: (updater: (value: NavbarStore) => NavbarStore, source = "unknown") => {
            updateCounter++;
            const id = updateCounter;
            const now = Date.now();
            const timeSinceLastUpdate = now - lastUpdateTime;

            return update((current) => {
                const updated = updater(current);

                lastUpdateSource = source;
                lastUpdateTime = now;

                return updated;
            });
        },
        getLastUpdateSource: () => lastUpdateSource,
    };
};

export const navbarStore = createDebugStore();

// Function to explicitly sync navbar counts with the backend
export async function syncNavbarCounts(): Promise<void> {
    try {
        const response = await ajax<NavbarResponse>(
            `${API_BASE_URL}/transcription/get/navbar_values/`,
            {},
            "json",
            "GET"
        );

        if (response && typeof response === "object" && "dictations" in response) {
            const dictations = response.dictations;

            navbarStore.update(
                (n) => ({
                    ...n,
                    todoCount: dictations.unverified || 0,
                    verifiedCount: dictations.verified || 0,
                    doneCount: dictations.done || 0,
                    overdueCount: dictations.overdue || 0,
                    totalDictationsCount: dictations.total || 0,
                }),
                "explicit-sync"
            );
        }
    } catch (error) {
        // Error handling is preserved but without console.error
    }
}
